import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDebtCoupons, fetchDebtDetailBulk } from '../../../hooks/useDebt';
import { useSendDebtMails } from '../../../hooks/useSubscribers';
import { usePlans } from '../../../hooks/usePlans';
import { useGroups } from '../../../hooks/useGroups';
import {
  DEBT_DETAIL_ITEMS_PER_PAGE,
  POSSESSOR_FILTER_OPTIONS,
} from '../constants/debtDetailConstants';
import {
  aggregateSelectedSummary,
  buildDebtReminderPayload,
  buildRowKey,
  parseCuotaPuraValue,
  sanitizeDebtDetailFilters,
} from '../utils/debtDetailUtils';

const INITIAL_FILTER = {
  plan: '',
  group: '',
  contractNumber: '',
  isPossessor: '',
  minOverdue: '',
  maxOverdue: '',
  cuotaPura: '',
};

const sanitizeNumericFilter = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const numeric = Number(value);
  return Number.isNaN(numeric) ? '' : String(Math.max(0, Math.floor(numeric)));
};

const sanitizeCuotaPuraFilter = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  return String(value).replace(/[^\d.,\s]/g, '');
};

export const useDebtDetailTable = () => {
  const { data: plans = [], isLoading: plansLoading } = usePlans();
  const { data: groups = [], isLoading: groupsLoading } = useGroups();
  const sendDebtMailsMutation = useSendDebtMails();

  const [filter, setFilter] = useState(INITIAL_FILTER);
  const [page, setPage] = useState(1);
  const [selectedMap, setSelectedMap] = useState({});
  const [notificationResults, setNotificationResults] = useState(null);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(DEBT_DETAIL_ITEMS_PER_PAGE);

  useEffect(() => {
    if (plans.length > 0 && !filter.plan) {
      setFilter((prev) => ({
        ...prev,
        plan: plans[0]?.uuid || '',
      }));
    }
  }, [plans, filter.plan]);

  const queryFilters = useMemo(() => {
    const params = {
      page,
      per_page: rowsPerPage,
    };

    if (filter.plan) params.plan = filter.plan;
    if (filter.group) params.group = filter.group;

    if (filter.contractNumber) {
      params.contract_number = filter.contractNumber.trim();
    }

    if (filter.isPossessor) {
      params.is_possessor = filter.isPossessor;
    }

    const minOverdue = Number(filter.minOverdue);
    if (!Number.isNaN(minOverdue) && filter.minOverdue !== '') {
      params.min_overdue = minOverdue;
    }

    const maxOverdue = Number(filter.maxOverdue);
    if (!Number.isNaN(maxOverdue) && filter.maxOverdue !== '') {
      params.max_overdue = maxOverdue;
    }

    return params;
  }, [filter, page, rowsPerPage]);

  const {
    data: debtResponse,
    isLoading: debtLoading,
    isFetching: debtFetching,
  } = useDebtCoupons(queryFilters);

  const groupedCoupons = debtResponse?.grouped_coupons || [];
  const summary = debtResponse?.summary || debtResponse?.page_summary || null;
  const pageSummary = debtResponse?.page_summary || null;
  const totalPages = debtResponse?.pages || 0;
  const totalCount = debtResponse?.total || 0;

  const selectedSummary = useMemo(
    () => aggregateSelectedSummary(selectedMap),
    [selectedMap]
  );

  const cuotaPuraValue = useMemo(
    () => parseCuotaPuraValue(filter.cuotaPura),
    [filter.cuotaPura]
  );

  const isCuotaPuraValid = Boolean(cuotaPuraValue && cuotaPuraValue > 0);

  const exportFilters = useMemo(
    () => sanitizeDebtDetailFilters(queryFilters),
    [queryFilters]
  );

  const openResultsDialog = useCallback((results) => {
    setNotificationResults(results);
    setResultsDialogOpen(true);
  }, []);

  const closeResultsDialog = useCallback(() => {
    setResultsDialogOpen(false);
    setNotificationResults(null);
  }, []);

  const handleFilterChange = useCallback((event) => {
    const { name, value } = event.target || {};
    if (!name) return;

    setFilter((prev) => {
      let nextValue = value;
      if (name === 'minOverdue' || name === 'maxOverdue') {
        nextValue = sanitizeNumericFilter(value);
      } else if (name === 'cuotaPura') {
        nextValue = sanitizeCuotaPuraFilter(value);
      }
      return {
        ...prev,
        [name]: nextValue,
      };
    });
    setPage(1);
  }, []);

  const handlePossessorChange = useCallback((value) => {
    setFilter((prev) => ({
      ...prev,
      isPossessor: value,
    }));
    setPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilter((prev) => ({
      ...INITIAL_FILTER,
      plan: plans[0]?.uuid || '',
    }));
    setPage(1);
  }, [plans]);

  const handlePageChange = useCallback((event, value) => {
    setPage(value);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value)) {
      setRowsPerPage(value);
      setPage(1);
    }
  }, []);

  const toggleCouponSelection = useCallback((header, coupon) => {
    if (!coupon?.uuid) return;

    const key = buildRowKey(header);

    setSelectedMap((prev) => {
      const currentEntry = prev[key] || { header, coupons: {} };
      const nextCoupons = { ...currentEntry.coupons };

      if (nextCoupons[coupon.uuid]) {
        delete nextCoupons[coupon.uuid];
      } else {
        nextCoupons[coupon.uuid] = coupon;
      }

      const nextState = { ...prev };
      if (Object.keys(nextCoupons).length === 0) {
        delete nextState[key];
      } else {
        nextState[key] = {
          header,
          coupons: nextCoupons,
        };
      }
      return nextState;
    });
  }, []);

  const toggleRowSelection = useCallback((header, coupons = [], forceSelect = null) => {
    const key = buildRowKey(header);
    setSelectedMap((prev) => {
      const currentEntry = prev[key] || { header, coupons: {} };
      const currentCoupons = currentEntry.coupons || {};
      const shouldSelectAll =
        forceSelect !== null
          ? forceSelect
          : Object.keys(currentCoupons).length !== coupons.length;

      const nextState = { ...prev };

      if (shouldSelectAll) {
        const nextCoupons = {};
        coupons.forEach((coupon) => {
          if (coupon?.uuid) {
            nextCoupons[coupon.uuid] = coupon;
          }
        });
        if (Object.keys(nextCoupons).length > 0) {
          nextState[key] = {
            header,
            coupons: nextCoupons,
          };
        }
      } else {
        delete nextState[key];
      }

      return nextState;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedMap({});
  }, []);

  const isCouponSelected = useCallback(
    (header, couponUuid) => {
      if (!couponUuid) return false;
      const key = buildRowKey(header);
      return Boolean(selectedMap[key]?.coupons?.[couponUuid]);
    },
    [selectedMap]
  );

  const isRowFullySelected = useCallback(
    (header, coupons = []) => {
      const key = buildRowKey(header);
      const entry = selectedMap[key];
      if (!entry) return false;
      const selectedCount = Object.keys(entry.coupons || {}).length;
      return selectedCount === coupons.length && coupons.length > 0;
    },
    [selectedMap]
  );

  const buildSendPayload = useCallback(
    (options) =>
      buildDebtReminderPayload(selectedMap, {
        ...options,
        cuotaPura: filter.cuotaPura,
      }),
    [filter.cuotaPura, selectedMap]
  );

  const selectAllContracts = useCallback(async () => {
    if (isSelectingAll) return;

    setIsSelectingAll(true);
    try {
      const { data: completeData = [] } = await fetchDebtDetailBulk(exportFilters);

      const nextState = {};
      completeData.forEach((item = {}) => {
        const header = item.header || {};
        const coupons = item.coupons || [];
        if (!coupons.length) return;

        const couponMap = {};
        coupons.forEach((coupon) => {
          if (coupon?.uuid) {
            couponMap[coupon.uuid] = coupon;
          }
        });

        if (Object.keys(couponMap).length > 0) {
          const key = buildRowKey(header);
          nextState[key] = {
            header,
            coupons: couponMap,
          };
        }
      });

      setSelectedMap(nextState);
    } catch (error) {
      console.error('Error selecting all contracts:', error);
    } finally {
      setIsSelectingAll(false);
    }
  }, [exportFilters, isSelectingAll]);

  return {
    plans,
    groups,
    plansLoading,
    groupsLoading,
    filter,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    data: groupedCoupons,
    summary,
    pageSummary,
    isLoading: debtLoading,
    isFetching: debtFetching,
    exportFilters,
    selectedMap,
    selectedSummary,
    cuotaPuraValue,
    isCuotaPuraValid,
    notificationResults,
    resultsDialogOpen,
    openResultsDialog,
    closeResultsDialog,
    isSelectingAll,
    selectAllContracts,
    possessorOptions: POSSESSOR_FILTER_OPTIONS,
    handleFilterChange,
    handlePossessorChange,
    handleResetFilters,
    handlePageChange,
    handleRowsPerPageChange,
    toggleCouponSelection,
    toggleRowSelection,
    isCouponSelected,
    isRowFullySelected,
    clearSelection,
    buildSendPayload,
    sendDebtMailsMutation,
  };
};
